import React, { useState, useEffect } from 'react';
import {
    Container,
    Box,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { transactionProcessorAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatDateTime } from '../../utils/dateUtils';

interface ProcessingLog {
    id: string;
    status: string;
    startedAt: string;
    completedAt: string;
    recurringTransactionsCreated: number;
    transactionsProcessed: number;
    transactionsSucceeded: number;
    transactionsFailed: number;
    errorMessage?: string;
    triggeredBy: string;
    triggeredByUser?: string;
}

const TransactionProcessor: React.FC = () => {
    const { user } = useAuth();
    const [logs, setLogs] = useState<ProcessingLog[]>([]);
    const [loadingLogs, setLoadingLogs] = useState(false);

    // Only allow SUPER_ADMIN
    const isSuperAdmin = user?.role === 'super_admin';

    useEffect(() => {
        if (isSuperAdmin) {
            fetchLogs();
        }
    }, [isSuperAdmin]);

    const fetchLogs = async () => {
        setLoadingLogs(true);
        try {
            const response = await transactionProcessorAPI.getLogs(20);
            setLogs(response.data.logs || []);
        } catch (err: any) {
            console.error('Error fetching processing logs:', err);
        } finally {
            setLoadingLogs(false);
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status.toUpperCase()) {
            case 'SUCCESS':
                return <CheckCircleIcon color="success" />;
            case 'PARTIAL_SUCCESS':
                return <WarningIcon color="warning" />;
            case 'FAILED':
                return <ErrorIcon color="error" />;
            case 'RUNNING':
                return <CircularProgress size={20} />;
            default:
                return <InfoIcon color="info" />;
        }
    };

    const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
        switch (status.toUpperCase()) {
            case 'SUCCESS':
                return 'success';
            case 'PARTIAL_SUCCESS':
                return 'warning';
            case 'FAILED':
                return 'error';
            case 'RUNNING':
                return 'info';
            default:
                return 'default';
        }
    };

    if (!isSuperAdmin) {
        return (
            <Container maxWidth="lg">
                <Box sx={{ mt: 4 }}>
                    <Alert severity="error">
                        Access Denied: Only Super Administrators can access this page.
                    </Alert>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Transaction Processor
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                    Processing history for the nightly transaction run. Shop and F&amp;B member-account charges are
                    processed immediately at checkout and are not part of the nightly run.
                </Typography>

                {/* Processing History */}
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            Processing History
                        </Typography>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchLogs} disabled={loadingLogs}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {loadingLogs ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : logs.length === 0 ? (
                        <Alert severity="info">
                            No processing history available
                        </Alert>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Started</TableCell>
                                        <TableCell>Completed</TableCell>
                                        <TableCell align="center">Recurring Created</TableCell>
                                        <TableCell align="center">Processed</TableCell>
                                        <TableCell align="center">Succeeded</TableCell>
                                        <TableCell align="center">Failed</TableCell>
                                        <TableCell>Triggered By</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logs.map((log) => (
                                        <TableRow key={log.id} hover>
                                            <TableCell>
                                                <Chip
                                                    icon={getStatusIcon(log.status)}
                                                    label={log.status.replace('_', ' ')}
                                                    color={getStatusColor(log.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{formatDateTime(log.startedAt)}</TableCell>
                                            <TableCell>
                                                {log.completedAt ? formatDateTime(log.completedAt) : '-'}
                                            </TableCell>
                                            <TableCell align="center">
                                                {log.recurringTransactionsCreated}
                                            </TableCell>
                                            <TableCell align="center">
                                                {log.transactionsProcessed}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography color="success.main">
                                                    {log.transactionsSucceeded}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                {log.transactionsFailed > 0 ? (
                                                    <Tooltip title={log.errorMessage || 'View details'}>
                                                        <Typography color="error.main">
                                                            {log.transactionsFailed}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography>{log.transactionsFailed}</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Chip
                                                        label={log.triggeredBy}
                                                        size="small"
                                                        color={log.triggeredBy === 'MANUAL' ? 'primary' : 'default'}
                                                    />
                                                    {log.triggeredByUser && (
                                                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                            {log.triggeredByUser}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            </Box>
        </Container>
    );
};

export default TransactionProcessor;
